import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

function formatTime(isoTimestamp) {
  try {
    return new Date(isoTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch {
    return isoTimestamp;
  }
}

const LEVEL_COLOR = {
  info: 'rgba(74,222,128,0.85)',
  warn: '#fb923c',
  error: '#f87171',
};

function ScanLog({ logs, scanning }) {
  const [expanded, setExpanded] = useState(true);
  const containerRef = useRef(null);
  const userScrolledUp = useRef(false);

  // Auto-expand when a scan starts
  useEffect(() => {
    if (scanning) setExpanded(true);
  }, [scanning]);

  // Auto-scroll within the log container only (never moves the outer page)
  useEffect(() => {
    const el = containerRef.current;
    if (!userScrolledUp.current && el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    userScrolledUp.current = !atBottom;
  };

  if (!logs || logs.length === 0) return null;

  return (
    <Box sx={{
      bgcolor: '#0a0814', borderRadius: 2,
      border: '1px solid rgba(255,255,255,0.07)',
      overflow: 'hidden',
    }}>
      {/* Collapsible header - matches ScanHistory style */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.2, cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <TerminalIcon sx={{ fontSize: 16, color: 'rgba(74,222,128,0.4)' }} />
          <Typography sx={{
            fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: 0.8, color: 'rgba(232,228,248,0.35)',
          }}>
            Scan Log
          </Typography>
          <Typography sx={{ fontSize: '0.63rem', color: 'rgba(232,228,248,0.2)' }}>
            {logs.length} {logs.length === 1 ? 'line' : 'lines'}
          </Typography>
          {scanning && (
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80', ml: 0.5,
              '@keyframes logPulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.3 },
              },
              animation: 'logPulse 1.5s ease-in-out infinite',
            }} />
          )}
        </Box>
        {expanded
          ? <KeyboardArrowUpIcon sx={{ fontSize: 18, color: 'rgba(232,228,248,0.3)' }} />
          : <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'rgba(232,228,248,0.3)' }} />}
      </Box>

      <Collapse in={expanded}>
        <Box
          ref={containerRef}
          onScroll={handleScroll}
          sx={{
            maxHeight: 280, overflowY: 'auto', px: 2, pb: 1.2,
            WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
            fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
            fontSize: '0.7rem', lineHeight: 1.65,
            borderTop: '1px solid rgba(255,255,255,0.04)',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
          }}
        >
          {logs.map((line) => (
            <Box key={line.index} sx={{ display: 'flex', gap: 1.2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <span style={{ color: 'rgba(232,228,248,0.18)', flexShrink: 0, userSelect: 'none' }}>
                {formatTime(line.timestamp)}
              </span>
              <span style={{ color: LEVEL_COLOR[line.level] || LEVEL_COLOR.info }}>
                {line.message}
              </span>
            </Box>
          ))}

        </Box>
      </Collapse>
    </Box>
  );
}

export default ScanLog;
